import { useMemo, useState } from "react";
import { Package, X } from "lucide-react";
import { TextInput } from "@normasteaching/norma-ui";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { isItemDisabled } from "../../ProductService/isItemDisabled.ts";
import { isRateKoVariant } from "../../ProductService/isRateKoVariant.ts";
import { PurchasableItemPickerModal } from "./PurchasableItemPickerModal.tsx";
import {
  B2BCourseGrantDraft,
  B2BCourseGrantDraftErrors,
  EMPTY_COURSE_GRANT_DRAFT,
} from "./b2bCourseGrantDrafts.ts";

const BENEFICIARY_FIELDS = [
  { key: "email", label: "Email" },
  { key: "firstName", label: "Nome" },
  { key: "lastName", label: "Cognome" },
] as const;

export function B2BCourseGrantsEditor({
  value,
  onChange,
  errors = [],
}: {
  value: B2BCourseGrantDraft[];
  onChange: (drafts: B2BCourseGrantDraft[]) => void;
  errors?: B2BCourseGrantDraftErrors[];
}) {
  const { products } = useAllNormasTeachingProducts();
  const [pickingIndex, setPickingIndex] = useState<number | null>(null);
  const courseProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          !isItemDisabled(product) &&
          !isRateKoVariant(product.variantProductName),
      ),
    [products],
  );
  const variantNameOf = (productId: string) =>
    products.find((product) => product._id === productId)?.variantProductName ??
    productId;

  const updateDraft = (index: number, changes: Partial<B2BCourseGrantDraft>) =>
    onChange(
      value.map((draft, i) => (i === index ? { ...draft, ...changes } : draft)),
    );

  return (
    <div className="flex flex-col gap-3">
      {value.map((draft, index) => (
        <div
          key={index}
          className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1.2fr_auto] gap-2 items-start rounded-lg border border-gray-200 p-3"
        >
          {BENEFICIARY_FIELDS.map(({ key, label }) => (
            <TextInput
              key={key}
              placeholder={label}
              aria-label={`${label} del partecipante ${index + 1}`}
              value={draft[key]}
              onChange={(e) => updateDraft(index, { [key]: e.target.value })}
              errorMessage={errors[index]?.[key]}
            />
          ))}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setPickingIndex(index)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <Package className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="truncate">
                {draft.productId
                  ? variantNameOf(draft.productId)
                  : "Scegli il corso"}
              </span>
            </button>
            {errors[index]?.productId && (
              <p className="text-xs text-red-500">{errors[index].productId}</p>
            )}
          </div>
          <button
            type="button"
            aria-label={`Rimuovi il partecipante ${index + 1}`}
            onClick={() => onChange(value.filter((_, i) => i !== index))}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...value, EMPTY_COURSE_GRANT_DRAFT])}
        className="self-start text-sm text-blue-600 hover:underline"
      >
        + Aggiungi partecipante
      </button>

      <PurchasableItemPickerModal
        isOpen={pickingIndex !== null}
        onClose={() => setPickingIndex(null)}
        products={courseProducts}
        bundles={[]}
        title="Scegli il corso da erogare"
        selected={
          pickingIndex !== null && value[pickingIndex]?.productId
            ? { type: "product", id: value[pickingIndex].productId }
            : undefined
        }
        onPick={(item) => {
          if (pickingIndex !== null)
            updateDraft(pickingIndex, { productId: item.id });
        }}
      />
    </div>
  );
}
